// Rybbit records a pageview on every history.pushState and history.replaceState.
// Inertia persists scroll position with replaceState while the URL stays put,
// so one page was logging a view about once a second. The document title is
// not a second navigation: SSR emits "Page - Calm Companies", then the client
// sets "Calm Companies | Page", and the next history write copied that title.

const installedHosts = new WeakSet<object>()

type HistoryMethod = (
  data: unknown,
  unused: string,
  url?: string | URL | null,
) => void

export interface PageviewGate {
  allow(pathname: string, search: string): boolean
}

export interface PageviewGuardHost {
  history: {
    pushState: HistoryMethod
    replaceState: HistoryMethod
  }
  location: { pathname: string; search: string; hash: string }
  fetch: typeof fetch
  addEventListener: (type: string, listener: () => void) => void
  rybbit?: { pageview?: () => void }
}

function normalizeSearch(search: string): string {
  if (search === "" || search === "?") return ""
  return search.startsWith("?") ? search : `?${search}`
}

// Stripe puts the Checkout Session id on the return URL. Drop it before a
// pageview is recorded. Other params, including success, stay.
export function searchWithoutCheckoutSession(search: string): string {
  const normalized = normalizeSearch(search)
  if (normalized === "" || !normalized.includes("session_id=")) return normalized

  const params = new URLSearchParams(normalized.slice(1))
  params.delete("session_id")
  const next = params.toString()
  return next === "" ? "" : `?${next}`
}

export function pageviewBodyWithoutCheckoutSession(body: string): string {
  let parsed: unknown
  try {
    parsed = JSON.parse(body)
  } catch {
    return body
  }
  if (typeof parsed !== "object" || parsed === null) return body

  const record = parsed as Record<string, unknown>
  if (record.type !== "pageview" || typeof record.querystring !== "string") {
    return body
  }

  const querystring = searchWithoutCheckoutSession(record.querystring)
  if (querystring === normalizeSearch(record.querystring)) return body

  return JSON.stringify({...record, querystring})
}

function pageviewKey(pathname: string, search: string): string {
  return `${pathname}${normalizeSearch(search)}`
}

export function pageKeyFromLocation(location: {
  pathname: string
  search: string
  hash: string
}): string {
  if (location.hash.startsWith("#/")) {
    try {
      const url = new URL(location.hash.slice(1), "https://calmcompanies.club")
      return pageviewKey(url.pathname, searchWithoutCheckoutSession(url.search))
    } catch {
      // Use the document path when the hash is not a URL.
    }
  }

  return pageviewKey(
    location.pathname,
    searchWithoutCheckoutSession(location.search),
  )
}

export function createPageviewGate(): PageviewGate {
  let lastKey: string | null = null

  return {
    allow(pathname, search) {
      const key = pageviewKey(pathname, searchWithoutCheckoutSession(search))
      if (lastKey === key) return false
      lastKey = key
      return true
    },
  }
}

function isTrackPath(url: string): boolean {
  try {
    const pathname = new URL(url, "https://calmcompanies.club").pathname
    return pathname === "/api/rv/track" || pathname.endsWith("/api/rv/track")
  } catch {
    return false
  }
}

function readPageview(body: BodyInit | null | undefined): {
  pathname: string
  search: string
} | null {
  if (typeof body !== "string") return null

  let parsed: unknown
  try {
    parsed = JSON.parse(body)
  } catch {
    return null
  }

  if (typeof parsed !== "object" || parsed === null) return null

  const record = parsed as Record<string, unknown>
  if (record.type !== "pageview" || typeof record.pathname !== "string") {
    return null
  }

  return {
    pathname: record.pathname,
    search: typeof record.querystring === "string" ? record.querystring : "",
  }
}

export function shouldDropDuplicatePageview(
  gate: PageviewGate,
  request: { url: string; method?: string; body?: BodyInit | null },
): boolean {
  if ((request.method ?? "GET").toUpperCase() !== "POST") return false
  if (!isTrackPath(request.url)) return false

  const pageview = readPageview(request.body)
  if (!pageview) return false

  return !gate.allow(
    pageview.pathname,
    searchWithoutCheckoutSession(pageview.search),
  )
}

export function rewriteTrackedPageview(
  request: { url: string; method?: string; body?: BodyInit | null },
): BodyInit | null | undefined {
  if ((request.method ?? "GET").toUpperCase() !== "POST") return request.body
  if (!isTrackPath(request.url)) return request.body
  if (typeof request.body !== "string") return request.body

  return pageviewBodyWithoutCheckoutSession(request.body)
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input
  if (input instanceof URL) return input.href
  return input.url
}

function requestParts(
  input: RequestInfo | URL,
  init?: RequestInit,
): { url: string; method: string; body?: BodyInit | null } {
  const method =
    init?.method ??
    (typeof input === "string" || input instanceof URL ? "GET" : input.method)

  return {
    url: requestUrl(input),
    method: method.toUpperCase(),
    body: init?.body,
  }
}

function installFetchGate(host: PageviewGuardHost, gate: PageviewGate): void {
  const nativeFetch = host.fetch.bind(host)

  host.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const parts = requestParts(input, init)
    let nextInit = init
    try {
      const body = rewriteTrackedPageview(parts)
      if (typeof body === "string" && body !== parts.body) {
        nextInit = {...init, body}
        parts.body = body
      }
      if (shouldDropDuplicatePageview(gate, parts)) {
        return new Response(null, { status: 204 })
      }
    } catch {
      // A bad analytics payload must not block the original request.
    }

    return nativeFetch(input, nextInit)
  }
}

function lockHistoryMethod(
  history: PageviewGuardHost["history"],
  name: "pushState" | "replaceState",
  wrapped: HistoryMethod,
): void {
  try {
    Object.defineProperty(history, name, {
      configurable: true,
      enumerable: true,
      get: () => wrapped,
      set: () => {
        // Rybbit assigns its own hook after the tracking config loads.
      },
    })
  } catch {
    history[name] = wrapped
  }
}

function watchRybbit(host: PageviewGuardHost, flush: () => void): void {
  let api = host.rybbit
  try {
    Object.defineProperty(host, "rybbit", {
      configurable: true,
      enumerable: true,
      get: () => api,
      set: (next: PageviewGuardHost["rybbit"]) => {
        api = next
        try {
          flush()
        } catch {
          // Analytics must never break snippet setup.
        }
      },
    })
  } catch {
    // If the property can't be observed, pageviews wait until the next URL change.
  }
}

function installHistoryGuard(host: PageviewGuardHost): void {
  const nativePush = host.history.pushState
  const nativeReplace = host.history.replaceState
  let observedKey = pageKeyFromLocation(host.location)
  let pending = false

  const callPageview = () => {
    const pageview = host.rybbit?.pageview
    if (typeof pageview !== "function") {
      pending = true
      return
    }

    pending = false
    pageview()
  }

  const onUrlChanged = () => {
    const nextKey = pageKeyFromLocation(host.location)
    if (nextKey === observedKey) return
    observedKey = nextKey
    callPageview()
  }

  const guard = (native: HistoryMethod): HistoryMethod => {
    return function (data, unused, url) {
      native.call(host.history, data, unused, url)
      try {
        onUrlChanged()
      } catch {
        // Analytics must never break navigation.
      }
    }
  }

  lockHistoryMethod(host.history, "pushState", guard(nativePush))
  lockHistoryMethod(host.history, "replaceState", guard(nativeReplace))
  host.addEventListener("popstate", () => {
    try {
      onUrlChanged()
    } catch {
      // Analytics must never break back/forward navigation.
    }
  })
  watchRybbit(host, () => {
    if (!pending) return
    callPageview()
  })
}

function stripCheckoutSessionFromLocation(host: PageviewGuardHost): void {
  const search = searchWithoutCheckoutSession(host.location.search)
  if (search === normalizeSearch(host.location.search)) return

  const next = `${host.location.pathname}${search}${host.location.hash}`
  host.history.replaceState(null, "", next)
}

export function installRybbitPageviewGuard(host?: PageviewGuardHost): void {
  if (!host && typeof window === "undefined") return

  try {
    const target = host ?? window
    if (installedHosts.has(target)) return
    installedHosts.add(target)
    stripCheckoutSessionFromLocation(target)
    installFetchGate(target, createPageviewGate())
    installHistoryGuard(target)
  } catch {
    // Analytics must never break the page.
  }
}
